import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import FilterSelect from '../../components/FilterSelect';
import { AdminPageHeader, AdminStatusBadge, AdminTableCard, useAdminList } from './AdminUi';
import './admin.css';

const EMPTY_FORM = {
  name: '',
  email: '',
  password: '',
  phone_number: '',
  specialization: '',
  qualification: '',
  experience: '',
  consultation_fees: '',
  audio_consultation_fees: '',
  address: '',
  verification_status: 'APPROVED',
};

const STATUS_OPTIONS = [
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'REJECTED', label: 'Rejected' },
];

export default function AdminDoctors() {
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const { data, loading, error, load } = useAdminList(
    () =>
      api.get('admin/doctors/', {
        params: { q: q || undefined, status: status || undefined, limit: 100 },
      }),
    [status]
  );

  const statusFilters = [
    { value: '', label: 'All' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' },
  ];

  const onFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const closeAdd = () => {
    setShowAdd(false);
    setForm(EMPTY_FORM);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        verification_status: form.verification_status,
      };
      if (form.phone_number.trim()) payload.phone_number = form.phone_number.trim();
      if (form.specialization.trim()) payload.specialization = form.specialization.trim();
      if (form.qualification.trim()) payload.qualification = form.qualification.trim();
      if (form.experience !== '') payload.experience = form.experience;
      if (form.consultation_fees !== '') payload.consultation_fees = form.consultation_fees;
      if (form.audio_consultation_fees !== '') {
        payload.audio_consultation_fees = form.audio_consultation_fees;
      }
      if (form.address.trim()) payload.address = form.address.trim();

      await api.post('admin/doctors/', payload);
      toast.success('Doctor created successfully');
      closeAdd();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create doctor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <AdminPageHeader title="Doctors" subtitle={`${data.total} doctors on platform`}>
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-primary rounded-pill px-3"
            onClick={() => setShowAdd(true)}
          >
            <i className="bi bi-plus-lg me-1" />
            Add Doctor
          </button>
          <form
            className="d-flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              load();
            }}
          >
            <input
              className="form-control"
              style={{ minWidth: 200 }}
              placeholder="Search name, email, specialty…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button className="btn btn-outline-primary rounded-pill px-3" type="submit">
              Search
            </button>
          </form>
        </div>
      </AdminPageHeader>

      <div className="admin-filter-chips mb-3">
        {statusFilters.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            className={`admin-chip ${status === f.value ? 'active' : ''}`}
            onClick={() => setStatus(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      ) : (
        <AdminTableCard>
          <table className="admin-table admin-table-people">
            <thead>
              <tr>
                <th>Doctor</th>
                <th>Contact</th>
                <th>Fees</th>
                <th>Rating</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.results.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-muted py-4">No doctors found</td></tr>
              ) : (
                data.results.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <div className="admin-person-cell">
                        <div className="admin-person-avatar">
                          {d.image ? (
                            <img src={d.image} alt="" />
                          ) : (
                            (d.name || 'D').slice(0, 1).toUpperCase()
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="fw-semibold text-truncate">{d.name}</div>
                          <div className="small text-muted text-truncate">
                            {d.specialization || 'No specialty'}
                            {d.experience != null ? ` · ${d.experience} yrs` : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="small fw-semibold text-truncate" style={{ maxWidth: 200 }}>{d.email}</div>
                      <div className="small text-muted">{d.phone_number || '—'}</div>
                    </td>
                    <td>
                      <div className="small">
                        <span className="text-muted">Video</span>{' '}
                        <strong>{d.consultation_fees != null ? `$${d.consultation_fees}` : '—'}</strong>
                      </div>
                      <div className="small">
                        <span className="text-muted">Audio</span>{' '}
                        <strong>{d.audio_consultation_fees != null ? `$${d.audio_consultation_fees}` : '—'}</strong>
                      </div>
                    </td>
                    <td>
                      <div className="admin-rating-cell">
                        <span className="star">★</span>
                        <strong>{Number(d.average_rating || 0).toFixed(1)}</strong>
                        <span className="text-muted">/5</span>
                      </div>
                      <div className="small text-muted">{d.total_reviews || 0} reviews</div>
                    </td>
                    <td><AdminStatusBadge status={d.verification_status} /></td>
                    <td>
                      <div className="admin-row-actions justify-content-end">
                        <Link
                          to={`/admin/doctors/${d.id}`}
                          className="admin-action-btn view"
                          title="View profile"
                        >
                          <i className="bi bi-eye" />
                          <span>View</span>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </AdminTableCard>
      )}

      {showAdd &&
        createPortal(
          <div
            className="admin-add-doctor-overlay"
            onClick={closeAdd}
            role="presentation"
          >
            <div
              className="admin-add-doctor-dialog"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="add-doctor-title"
            >
              <form onSubmit={handleCreate} className="admin-add-doctor-form">
                <div className="admin-add-doctor-header">
                  <div>
                    <h5 id="add-doctor-title" className="fw-bold mb-0">
                      Add Doctor
                    </h5>
                    <p className="small text-muted mb-0">
                      Create an account and set status (default: Approved)
                    </p>
                  </div>
                  <button type="button" className="btn-close" onClick={closeAdd} aria-label="Close" />
                </div>

                <div className="admin-add-doctor-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Full name *</label>
                      <input
                        className="form-control"
                        name="name"
                        value={form.name}
                        onChange={onFormChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Email *</label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={form.email}
                        onChange={onFormChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Password *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="password"
                        value={form.password}
                        onChange={onFormChange}
                        minLength={6}
                        required
                        autoComplete="new-password"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Phone</label>
                      <input
                        className="form-control"
                        name="phone_number"
                        value={form.phone_number}
                        onChange={onFormChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Specialization</label>
                      <input
                        className="form-control"
                        name="specialization"
                        value={form.specialization}
                        onChange={onFormChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Qualification</label>
                      <input
                        className="form-control"
                        name="qualification"
                        value={form.qualification}
                        onChange={onFormChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Experience (years)</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        name="experience"
                        value={form.experience}
                        onChange={onFormChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Video fee ($)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control"
                        name="consultation_fees"
                        value={form.consultation_fees}
                        onChange={onFormChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Audio fee ($)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control"
                        name="audio_consultation_fees"
                        value={form.audio_consultation_fees}
                        onChange={onFormChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Status</label>
                      <FilterSelect
                        value={form.verification_status}
                        onChange={(val) =>
                          setForm((prev) => ({ ...prev, verification_status: val }))
                        }
                        options={STATUS_OPTIONS}
                        variant="bordered"
                        aria-label="Verification status"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Address</label>
                      <textarea
                        className="form-control"
                        name="address"
                        rows={2}
                        value={form.address}
                        onChange={onFormChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="admin-add-doctor-footer">
                  <button type="button" className="btn btn-light rounded-pill px-3" onClick={closeAdd}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary rounded-pill px-4"
                    disabled={saving}
                  >
                    {saving ? 'Creating…' : 'Create Doctor'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
